/**
 * Match-server WebSocket client. The server is authoritative: it sends `{t:'welcome',side}` once,
 * then `{t:'state',state}` broadcasts ALREADY in this client's perspective (you at the bottom). We
 * only send `{t:'action',action}`. So the client stays a dumb renderer — no perspective logic here.
 */
import type { Action, GameState, Side } from '../sim/types.ts';

const DEFAULT_SERVER = 'wss://fairway-clash-server.nathanwinkelmes.workers.dev';

/** The match-server base URL. Override for local dev via `localStorage['fc-server']`
 *  (e.g. `ws://localhost:8787`). */
export function serverBase(): string {
  if (typeof localStorage !== 'undefined') {
    const override = localStorage.getItem('fc-server');
    if (override) return override;
  }
  return DEFAULT_SERVER;
}

/** Match URL, optionally carrying the player token (→ rating updates) + the mode (ranked rates). */
export function matchUrl(matchId: string, opts?: { token?: string | null; mode?: string }): string {
  const q = new URLSearchParams();
  if (opts?.mode) q.set('mode', opts.mode);
  if (opts?.token) q.set('token', opts.token);
  const qs = q.toString();
  return `${serverBase()}/match/${matchId}${qs ? `?${qs}` : ''}`;
}

export function matchmakeUrl(mode: string): string {
  return `${serverBase()}/matchmake?mode=${encodeURIComponent(mode)}`;
}

export interface FindMatchHandlers {
  onSearching?(): void;
  /** Paired — connect to this match id. */
  onMatched(matchId: string): void;
  onClose?(): void;
}

export interface Matchmaking {
  cancel(): void;
}

/** Queue for an opponent in `mode` ('ranked' | 'casual'). Resolves to a match id via `onMatched`. */
export function findMatch(mode: string, handlers: FindMatchHandlers): Matchmaking {
  const ws = new WebSocket(matchmakeUrl(mode));
  let matched = false;
  ws.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') return;
    let msg: { t?: string; matchId?: string };
    try {
      msg = JSON.parse(event.data) as typeof msg;
    } catch {
      return;
    }
    if (msg.t === 'searching') handlers.onSearching?.();
    else if (msg.t === 'matched' && msg.matchId) {
      matched = true;
      handlers.onMatched(msg.matchId);
    }
  });
  ws.addEventListener('close', () => {
    if (!matched) handlers.onClose?.();
  });
  return {
    cancel(): void {
      ws.close();
    },
  };
}

export interface NetMatchHandlers {
  onWelcome?(side: Side): void;
  onState(state: GameState): void;
  /** Lobby status: how many players are connected + whether the match has started. */
  onStatus?(players: number, started: boolean): void;
  onClose?(): void;
}

export interface NetMatch {
  send(action: Action): void;
  close(): void;
}

/** Open a live match connection. */
export function connectMatch(url: string, handlers: NetMatchHandlers): NetMatch {
  const ws = new WebSocket(url);
  ws.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') return;
    let msg: { t?: string; side?: Side; state?: GameState; players?: number; started?: boolean };
    try {
      msg = JSON.parse(event.data) as typeof msg;
    } catch {
      return;
    }
    if (msg.t === 'welcome' && (msg.side === 'you' || msg.side === 'rival')) handlers.onWelcome?.(msg.side);
    else if (msg.t === 'state' && msg.state) handlers.onState(msg.state);
    else if (msg.t === 'status') handlers.onStatus?.(msg.players ?? 0, msg.started ?? false);
  });
  ws.addEventListener('close', () => handlers.onClose?.());
  return {
    send(action): void {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'action', action }));
    },
    close(): void {
      ws.close();
    },
  };
}
